import axios from 'axios';
import type { TFunction } from 'i18next';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  type SendTransitDdsError,
  useSendTransitDds,
} from '@/services/apis/mutations/transits/use-send-transit-dds';
import { getDdsErrorMessage } from '../dds-error-message';

function errorMessage(
  error: unknown,
  t: TFunction<'transits'>,
  isAmendment: boolean,
): string {
  if (!axios.isAxiosError<SendTransitDdsError>(error)) {
    return t(isAmendment ? 'ddsAmendError' : 'ddsSendError');
  }
  const data = error.response?.data;
  if (data?.ddsError) {
    return getDdsErrorMessage(
      {
        ...data.ddsError,
        submissionUncertain:
          data.ddsSubmissionUncertain ?? data.ddsError.submissionUncertain,
      },
      t,
    );
  }
  if (data?.ddsSubmissionUncertain) {
    return t('ddsSubmissionUncertain');
  }
  if (data?.ddsRejectionCodes?.length || data?.issues?.length) {
    return getDdsErrorMessage(
      {
        operation: isAmendment ? 'amendDds' : 'submitDds',
        category: 'preparation',
        issues: data.issues,
        rejectionCodes: data.ddsRejectionCodes,
      },
      t,
    );
  }
  switch (error.response?.status) {
    case 409:
      return t(isAmendment ? 'ddsAmendConflict' : 'ddsSendConflict');
    case 403:
      return t(isAmendment ? 'ddsAmendForbidden' : 'ddsSendForbidden');
    case 503:
      return t('ddsServiceUnavailable');
    default:
      return t(isAmendment ? 'ddsAmendError' : 'ddsSendError');
  }
}

export function useTransitDdsSend(
  transitId: string,
  purchaseOrderId: number,
  isAmendment = false,
) {
  const { t } = useTranslation('transits');
  const toastId = useRef<string | number | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const sending = useSendTransitDds(
    transitId,
    purchaseOrderId,
    {
      onSuccess: () =>
        toast.success(t(isAmendment ? 'ddsAmendSuccess' : 'ddsSendSuccess')),
      onError: (error) => {
        toastId.current = toast.error(errorMessage(error, t, isAmendment), {
          duration: Number.POSITIVE_INFINITY,
          closeButton: true,
        });
      },
    },
    isAmendment ? 'amend' : 'send',
  );

  const send = () => {
    if (toastId.current !== undefined) {
      toast.dismiss(toastId.current);
      toastId.current = undefined;
    }
    setMessage(null);
    sending.mutate(undefined, {
      onSuccess: () => {
        setIsOpen(false);
      },
      onError: (error) => {
        setMessage(errorMessage(error, t, isAmendment));
      },
    });
  };

  return {
    isOpen,
    setIsOpen,
    errorMessage: message,
    send,
    isSending: sending.isPending,
  };
}
